export const initKeycloakClient = (context, event) => (sendBack, onReceive) => {
  (async () => {
    try {
      const authenticated = await context.keycloak.init({
        onLoad: "login-required",
        checkLoginIframe: false,
      });
      console.log("Login result", authenticated);
    } catch (error) {
      console.log("Login error", error);
    }
  })();

  return () => {};
};

export const loadUserProfile = (context, event) => {
  const { keycloak, timeout = 15000 } = context;

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(
        new Error("User profile load timeout - no response from keycloak"),
      );
    }, timeout);

    keycloak
      .loadUserProfile()
      .then((profile) => {
        clearTimeout(timeoutId);
        resolve(profile);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        const errorObj = {
          type: "KEYCLOAK_LOAD_PROFILE_ERROR",
          message: error?.message || "Failed to load user profile",
          originalError: error,
          timestamp: Date.now(),
        };
        reject(errorObj);
      });
  });
};

export const redirectToLoginForm = (context, event) => {
  const { keycloak, timeout = 15000 } = context;

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Login timeout - no response from keycloak"));
    }, timeout);

    keycloak
      .login()
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        // Structured error reporting
        const errorObj = {
          type: "KEYCLOAK_LOGIN_ERROR",
          message: error?.message || "Login failed",
          originalError: error,
          timestamp: Date.now(),
        };
        reject(errorObj);
      });
  });
};

export const redirectToLogoutForm = (context, event) => {
  const { keycloak, timeout = 15000 } = context;

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Logout timeout - no response from keycloak"));
    }, timeout);

    keycloak
      .logout()
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        const errorObj = {
          type: "KEYCLOAK_LOGOUT_ERROR",
          message: error?.message || "Logout failed",
          originalError: error,
          timestamp: Date.now(),
        };
        reject(errorObj);
      });
  });
};

export const refreshToken = (context, event) => {
  const { keycloak, timeout = 15000 } = context;
  return new Promise((resolve, reject) => {
    // Set up a timeout as fallback
    const timeoutId = setTimeout(() => {
      reject(new Error("Token refresh timeout - no response from keycloak"));
    }, timeout);

    keycloak
      .updateToken(-1)
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        // Ensure we have a proper error structure
        const errorObj = {
          type: "KEYCLOAK_REFRESH_ERROR",
          message: error?.message || "Token refresh failed",
          originalError: error,
          timestamp: Date.now(),
        };
        reject(errorObj);
      });
  });
};

export const observeTokenExpiration =
  (context, event) => (sendBack, onReceive) => {
    const interval = setInterval(() => {
      if (context?.keycloak?.token && !context?.keycloak?.isTokenExpired()) {
        sendBack({ type: "TOKEN_EXPIRATION_UPDATED" });
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  };

export const setKeycloakEventsHandlers =
  (context, event) => (sendBack, onReceive) => {
    if (context?.keycloak) {
      context.keycloak.onTokenExpired = () => {
        sendBack({ type: "LOGOUT" });
      };
      context.keycloak.onAuthError = () => {
        console.log("Keycloak authentication failed");
        sendBack({
          type: "AUTH_ERROR",
          data: {
            error: new Error("An error occured during authentication"),
          },
        });
      };
      context.keycloak.onAuthRefreshSuccess = () => {
        console.log("Keycloak authentication token refreshed");
        //sendBack({ type: "TOKEN_REFRESHED" });
      };
      context.keycloak.onAuthSuccess = () => {
        console.log("Keycloak authentication successful");
        sendBack({ type: "AUTH_SUCCESS" });
      };
      context.keycloak.onReady = (authenticated) => {
        console.log("Keycloak is ready");
        console.log("Keycloak authenticated:", authenticated);
      };
      context.keycloak.onAuthRefreshError = () => {
        console.log("Keycloak authentication token refresh failed");
        sendBack({
          type: "TOKEN_REFRESH_ERROR",
          data: {
            error: new Error("An error occured during token refresh"),
          },
        });
      };
    }

    return () => {
      console.log("Cleanup Keycloak event handlers");
      context.keycloak.onTokenExpired = null;
      context.keycloak.onAuthError = null;
      context.keycloak.onAuthRefreshSuccess = null;
      context.keycloak.onAuthSuccess = null;
      context.keycloak.onReady = null;
      context.keycloak.onAuthRefreshError = null;
    };
  };

export const observeUserMouseActivity =
  (context, event) => (sendBack, onReceive) => {
    const listener = () => {
      sendBack({ type: "ACTIVATE" });
    };

    window.addEventListener("mousemove", listener);

    return () => {
      window.removeEventListener("mousemove", listener);
    };
  };

export const observeDocumentVisibilityState =
  (context, event) => (sendBack, onReceive) => {
    const listener = () => {
      if (document.visibilityState === "visible") {
        if (context?.keycloak?.token && context?.keycloak?.isTokenExpired()) {
          sendBack({ type: "LOGOUT" });
        }
      }
    };

    document.addEventListener("visibilitychange", listener);

    return () => {
      document.removeEventListener("visibilitychange", listener);
    };
  };

export const observeUserKeyboardActivityAny =
  (context, event) => (sendBack, onReceive) => {
    const listener = () => {
      sendBack({ type: "ACTIVATE" });
    };

    window.addEventListener("keydown", listener);

    return () => {
      window.removeEventListener("keydown", listener);
    };
  };

export const observeTokenExpiringWithin30seconds =
  (context, event) => (sendBack, onReceive) => {
    const interval = setInterval(() => {
      if (context?.keycloak?.token && context?.keycloak?.isTokenExpired(30)) {
        sendBack({ type: "TOKEN_EXPIRING" });
      } else {
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  };
